import { apiRequest } from "../utils/Helpers";
import appConfig from "../../config/config.dev";

/**
 * Fetch term suggestions from UpContent for automatic curation
 * @param searchTerm The term to search for (min 3 characters)
 * @param streamType The stream type: "ps" (Personal) or "pf" (Professional)
 * @param lang Language code (e.g., "en", "fr", "de")
 * @returns Array of term suggestions with termName and termType (matching/related)
 */
export async function doGetUpContentTermSuggestions(
    searchTerm: string,
    streamType: "ps" | "pf",
    lang: string = "en"
): Promise<Array<{ termName: string; termType: "matching" | "related" }>> {
    if (searchTerm.length < 3) {
        return [];
    }

    try {
        const response = await apiRequest<Array<{ termName: string; termType: "matching" | "related" }>>(
            `${appConfig.API_URL}/upcontentAutomaticCurationTerms`,
            "GET",
            {
                q: searchTerm,
                type: streamType,
                lang
            }
        );

        if (Array.isArray(response)) {
            return response;
        }
        return [];
    } catch (error) {
        console.error("Failed to fetch UpContent term suggestions:", error);
        return [];
    }
}

/**
 * Validate UpContent Account ID
 * @param accountId The UpContent account ID to validate
 * @param cid The client ID
 * @returns Validation result
 */
export async function doValidateUpContentAccountId(
    accountId: string,
    cid: string
): Promise<{ valid: boolean; message?: string }> {
    try {
        const response = await apiRequest<{ valid: boolean; message?: string }>(
            `${appConfig.API_URL}/validateUpContentAccountId`,
            "POST",
            {
                accountId,
                cid
            }
        );

        return response || { valid: false, message: "No response received" };
    } catch (error) {
        console.error("Failed to validate UpContent account ID:", error);
        return { valid: false, message: "Validation failed" };
    }
}

/**
 * Get UpContent Filter Views
 * @param accountId The UpContent account ID
 * @param cid The client ID
 * @returns Filter views data
 */
export async function doGetUpContentFilterViews(
    accountId: string,
    cid: string
): Promise<{ valid: boolean; data?: any[]; message?: string }> {
    try {
        const response = await apiRequest<{ valid: boolean; data?: any[]; message?: string }>(
            `${appConfig.API_URL}/getUpContentFilterViews`,
            "POST",
            {
                accountId,
                cid
            }
        );

        return response || { valid: false, message: "No response received" };
    } catch (error) {
        console.error("Failed to get UpContent filter views:", error);
        return { valid: false, message: "Failed to load filter views" };
    }
}

/**
 * Get UpContent Content Policies
 * @param accountId The UpContent account ID
 * @param cid The client ID
 * @returns Content policies data
 */
export async function doGetUpContentContentPolicies(
    accountId: string,
    cid: string
): Promise<{ valid: boolean; data?: any[]; message?: string }> {
    try {
        const response = await apiRequest<{ valid: boolean; data?: any[]; message?: string }>(
            `${appConfig.API_URL}/getUpContentContentPolicies`,
            "POST",
            {
                accountId,
                cid
            }
        );

        return response || { valid: false, message: "No response received" };
    } catch (error) {
        console.error("Failed to get UpContent content policies:", error);
        return { valid: false, message: "Failed to load content policies" };
    }
}

/**
 * Get UpContent Folders
 * @param accountId The UpContent account ID
 * @param cid The client ID
 * @returns Folders data
 */
export async function doGetUpContentFolders(
    accountId: string,
    cid: string
): Promise<{ valid: boolean; data?: any[]; message?: string }> {
    try {
        const response = await apiRequest<{ valid: boolean; data?: any[]; message?: string }>(
            `${appConfig.API_URL}/getUpContentFolders`,
            "POST",
            {
                accountId,
                cid
            }
        );

        return response || { valid: false, message: "No response received" };
    } catch (error) {
        console.error("Failed to get UpContent folders:", error);
        return { valid: false, message: "Failed to load folders" };
    }
}

/**
 * Sync terms to UpContent immediately
 * @param cid The client ID
 * @param newTerms Array of new terms to sync
 * @param oldTerms Array of old terms for comparison
 * @param searchType The search type (PERS or PROF)
 * @returns Sync result
 */
export async function doSyncTermsToUpContent(
    cid: string,
    newTerms: Array<{ term: string; checked: boolean }>,
    oldTerms: Array<{ term: string; checked: boolean }> = [],
    searchType: string
): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
        const response = await apiRequest<{ success: boolean; message?: string; error?: string }>(
            `${appConfig.API_URL}/syncTermsToUpContent`,
            "POST",
            {
                cid,
                newTerms,
                oldTerms,
                searchType
            }
        );

        if (response && typeof response === 'object' && 'success' in response) {
            return response;
        }
        return { success: false, error: "Invalid response received" };
    } catch (error) {
        console.error("Failed to sync terms to UpContent:", error);
        return { success: false, error: "Failed to sync terms" };
    }
}

/**
 * Get migration term count for UpContent migration
 * @param cid The client ID
 * @returns Term count and user count
 */
export async function doGetMigrationTermCount(
    cid: string
): Promise<{ valid: boolean; termCount?: number; userCount?: number; message?: string }> {
    try {
        const response = await apiRequest<{ valid: boolean; termCount?: number; userCount?: number; message?: string }>(
            `${appConfig.API_URL}/client/get-migration-term-count`,
            "POST",
            { cid }
        );

        return response || { valid: false, message: "No response received" };
    } catch (error) {
        console.error("Failed to get migration term count:", error);
        return { valid: false, message: "Failed to get term count" };
    }
}

/**
 * Trigger UpContent migration for a client
 * @param cid The client ID
 * @returns Migration result with success/failure details
 */
export async function doMigrateUpContent(
    cid: string
): Promise<{
    valid: boolean;
    migrationResult?: {
        results?: Array<{
            successfulTermsCount: number;
            failedTermsCount: number;
            successfulTerms: string[];
            failedTerms: string[];
        }>;
    };
    message?: string;
}> {
    try {
        const response = await apiRequest<{
            valid: boolean;
            migrationResult?: any;
            message?: string;
        }>(
            `${appConfig.API_URL}/client/migrate-upcontent`,
            "POST",
            { cid }
        );

        return response || { valid: false, message: "No response received" };
    } catch (error) {
        console.error("Failed to migrate UpContent:", error);
        return { valid: false, message: "Migration failed" };
    }
}

/**
 * Get migration status using dedicated migration-status endpoint
 * @param cid The client ID
 * @returns Structured migration status with progress and results
 */
export async function doGetMigrationStatus(
    cid: string
): Promise<{
    valid: boolean;
    status?: string;
    isComplete?: boolean;
    progress?: {
        completed: number;
        total: number;
        percentage: number;
    };
    results?: {
        successfulCount: number;
        failedCount: number;
        successfulTerms?: any[];
        failedTerms?: any[];
    };
    completedAt?: string;
    message?: string;
}> {
    try {
        const response = await apiRequest<any>(
            `${appConfig.API_URL}/client/migration-status`,
            "POST",
            { cid }
        );

        return response || { valid: false, message: "No response received" };
    } catch (error) {
        console.error("Failed to get migration status:", error);
        return { valid: false, message: "Failed to get migration status" };
    }
}

/**
 * Mark migration results as viewed
 * @param cid The client ID
 * @returns Success status
 */
export async function doMarkMigrationResultsViewed(
    cid: string
): Promise<{ valid: boolean; message?: string }> {
    try {
        const response = await apiRequest<{ valid: boolean; message?: string }>(
            `${appConfig.API_URL}/client/mark-migration-viewed`,
            "POST",
            { cid }
        );

        return response || { valid: false, message: "No response received" };
    } catch (error) {
        console.error("Failed to mark migration results as viewed:", error);
        return { valid: false, message: "Failed to mark results as viewed" };
    }
}

/**
 * Download migration results as CSV
 * @param cid The client ID
 * @returns Blob containing CSV data
 */
export async function doDownloadMigrationResultsCSV(
    cid: string
): Promise<{ success: boolean; blob?: Blob; error?: string }> {
    try {
        const response = await fetch(`${appConfig.API_URL}/client/migration-results-csv`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ cid })
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ message: 'Failed to download CSV' }));
            return { success: false, error: errorData.message || 'Failed to download CSV' };
        }

        const blob = await response.blob();
        return { success: true, blob };
    } catch (error) {
        console.error("Failed to download migration results CSV:", error);
        return { success: false, error: "Network error occurred" };
    }
}

